// SPDX-License-Identifier: AGPL-3.0-or-later

import {Routes} from '@app/app/Routes';
import {RootComponent} from '@app/app/router/components/RootComponent';
import {NotFoundPage} from '@app/features/app/components/pages/NotFoundPage';
import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import Authentication from '@app/features/auth/state/Authentication';
import {getDefaultLandingPath} from '@app/features/navigation/utils/DefaultLandingUtils';
import * as RouterUtils from '@app/features/navigation/utils/RouterUtils';
import {createRootRoute, createRoute} from '@app/features/platform/components/router/RouterBuilder';
import {Redirect} from '@app/features/platform/components/router/RouterTypes';
import SessionManager from '@app/features/platform/state/AuthSession';
import {observer} from 'mobx-react-lite';
import {lazy, Suspense, useEffect} from 'react';

export const rootRoute = createRootRoute({
	layout: ({children}) => (
		<RootComponent data-flx="app.router.root-routes.layout.root-component">{children}</RootComponent>
	),
});
export const notFoundRoute = createRoute({
	id: '__notFound',
	path: '/__notfound',
	component: () => <NotFoundPage data-flx="app.router.root-routes.not-found-page" />,
});
export const homeRoute = createRoute({
	getParentRoute: () => rootRoute,
	id: 'home',
	path: '/',
	component: observer(function HomeEntry() {
		const snapshot = RuntimeConfig.getSnapshotOrNull();
		useEffect(() => {
			if (!snapshot) {
				RouterUtils.replaceWith(Routes.LOGIN);
				return;
			}
			let active = true;
			void SessionManager.initialize()
				.then(() => {
					if (!active) return;
					RouterUtils.replaceWith(
						Authentication.isAuthenticated
							? getDefaultLandingPath()
							: snapshot.community.single_community && snapshot.features.self_hosted
								? '/community'
								: Routes.LOGIN,
					);
				})
				.catch(() => {
					if (active) RouterUtils.replaceWith(Routes.LOGIN);
				});
			return () => {
				active = false;
			};
		}, [snapshot]);
		return null;
	}),
});
export const appRoute = createRoute({
	getParentRoute: () => rootRoute,
	id: 'app',
	path: Routes.APP,
	onEnter: () => new Redirect(getDefaultLandingPath()),
});

const PublicCommunityPage = lazy(() => import('@app/features/community/PublicCommunityPage'));
export const publicCommunityRoute = createRoute({
	getParentRoute: () => rootRoute,
	id: 'publicCommunity',
	path: '/community',
	component: () => (
		<Suspense fallback={<div role="status">opening the community…</div>}>
			<PublicCommunityPage />
		</Suspense>
	),
});
